import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { Icon } from '../Common';
import { MapState } from '../../../common';
import {
	API_FAILURE_MSG,
	BOGLE_REGULAR,
	ITEM_DATA_NOT_AVAILABLE,
} from '../../../constants/constant';
import { UserPortalContext } from '../../../context/appcontext';
import { useMapSummaryStyles, mapSummaryStyles as styles } from './styles';
import { getItemNetSalesMapSummary } from '../../../redux/actions/itemDrillDownActions';
import { convertToTwoDecimal } from '../../../helper/convertAmount';
import ErrorScreenMessage from '../../ErrorScreenMessage/ErrorScreenMessage';
import {
	onHover,
	fillColor,
	getTooltip,
	formatNetSales,
	getLegendSlots,
	getColorSlotMap,
	getExcelDownloadData,
	formatNumberWithUnits,
} from './utils';
import DownloadMap from '../../Download/DownloadMap';
import { chartTitleChange } from '../../../helper/yoyUtils';
// import DefinitionDrawer, {
// 	DefinitionContext,
// } from '../../../components/Definition/index';
// import { getChartType } from '../../../components/Definition';
import { colors } from '../../../theme/color-palette';

const { HEATMAP, SUCCESS_MD, ERROR_MD, BLACK } = colors;
const { POSITIVE_COLORS, NEGATIVE_COLORS } = HEATMAP;

const chartTitle = 'Net Sales YoY Change by State (Stores only)';

function ItemHeatMap({
	weekFilter,
	category,
	itemNum,
	startDate,
	endDate,
	selectedTab,
}) {
	const classes = useMapSummaryStyles();
	const portalUserContext = React.useContext(UserPortalContext);
	const dispatch = useDispatch();
	const { apiKey } = useSelector((state) => state.itemDrillDownReducer.map);
	const {
		netSalesMapSummary,
		netSalesMapSummaryLoading,
		netSalesMapSummaryErrorCode,
	} = useSelector((state) => state.itemDrillDownReducer);
	const selectedView = useSelector(
		(state) => state.appGuideReducer.selectedView
	);

	const movement = netSalesMapSummary?.summary?.yoy > 0 ? 'up' : 'down';

	const [toggleValue, setToggleValue] = useState('percentage');

	const urlParams = new URLSearchParams(window.location.search);
	const subCatId = urlParams.get('subCatId');
	const brandId = urlParams.get('brandId');
	// const initialCxt = {
	// 	isDrawerOpened: false,
	// 	metricType: '',
	// };
	// const [definition, setDefinition] = useState(initialCxt);
	// const cxtValue = React.useMemo(
	// 	() => ({ definition, setDefinition }),
	// 	[definition]
	// );

	const handleToggleBtn = (e, value) => {
		setToggleValue(value);
	};
	// const openDefinition = () => {
	// 	setDefinition({
	// 		isDrawerOpened: true,
	// 		metricType: getChartType(chartTitle),
	// 	});
	// };
	useEffect(() => {
		if (itemNum) {
			const params = {
				categoryId: '-1',
				period: weekFilter,
				portalUserContext,
				itemNum,
				subCatId,
				brandId,
				startDate,
				endDate,
			};
			dispatch(getItemNetSalesMapSummary(params));
		}
	}, [
		weekFilter,
		category,
		itemNum,
		subCatId,
		brandId,
		startDate,
		endDate,
		selectedView,
	]);

	if (!netSalesMapSummaryLoading) {
		const stateGeoJSONData = netSalesMapSummary?.stateGeoJSONData;
		if (
			(netSalesMapSummaryErrorCode === 0 &&
				stateGeoJSONData?.features?.length === 0) ||
			netSalesMapSummaryErrorCode === 204
		) {
			return (
				<ErrorScreenMessage
					errorMsg={ITEM_DATA_NOT_AVAILABLE}
					chartTitle={chartTitle}
				/>
			);
		} else if (netSalesMapSummaryErrorCode !== 0) {
			return (
				<ErrorScreenMessage
					errorMsg={API_FAILURE_MSG}
					chartTitle={chartTitle}
				/>
			);
		}
	}

	const isPercentageChange = toggleValue === 'percentage';

	// Map legend
	const min = isPercentageChange
		? netSalesMapSummary.minYoY
		: netSalesMapSummary.minAbsYoY;
	const max = isPercentageChange
		? netSalesMapSummary.maxYoY
		: netSalesMapSummary.maxAbsYoY;

	const { POSITIVE_SLOTS, NEGATIVE_SLOTS } = getLegendSlots(min, max);
	const colorSlotMap = getColorSlotMap(POSITIVE_SLOTS, NEGATIVE_SLOTS);

	const prefix = isPercentageChange ? '' : '$';
	const suffix = isPercentageChange ? '%' : '';

	// Map
	let map = null;
	if (isPercentageChange) {
		map = (
			<Box sx={{ width: '100%', height: '100%' }}>
				<MapState
					key={0}
					apiKey={apiKey}
					width={'100%'}
					height={'100%'}
					tooltip={getTooltip(isPercentageChange)}
					onHover={onHover}
					fillColor={fillColor(colorSlotMap, 'yoyValue')}
					geoJSONData={netSalesMapSummary?.stateGeoJSONData}
				/>
			</Box>
		);
	} else {
		map = (
			<Box sx={{ width: '100%', height: '100%' }}>
				<MapState
					key={1}
					apiKey={apiKey}
					width={'100%'}
					height={'100%'}
					tooltip={getTooltip(isPercentageChange)}
					onHover={onHover}
					fillColor={fillColor(colorSlotMap, 'absYoY')}
					geoJSONData={netSalesMapSummary?.stateGeoJSONData}
				/>
			</Box>
		);
	}

	const handleNullorUndefined = (num) => {
		return typeof num !== 'undefined' && num !== null ? num : '-';
	};

	return (
		<Fragment>
			{netSalesMapSummaryLoading || !apiKey ? (
				<Box className={classes.loader}>
					<CircularProgress className={classes.circularProgress} />
				</Box>
			) : (
				<Fragment>
					<Box className={classes.header}>
						<Box>
							<Typography
								style={styles.title}
								//	 onClick={openDefinition}
							>
								{chartTitle}
							</Typography>
							<Typography style={styles.value}>
								{formatNetSales(netSalesMapSummary?.summary?.total)}
							</Typography>
							<Icon movement={movement} />
							<Typography style={styles.yoy} color={styles[movement].color}>
								{`${chartTitleChange(
									convertToTwoDecimal(netSalesMapSummary?.summary?.yoy)
								)}`}
							</Typography>
							<Typography style={styles.yoyLabel}>(YoY)</Typography>
						</Box>
						<Box className={classes.actions}>
							<Typography style={styles.export}>
								<DownloadMap
									data={getExcelDownloadData(netSalesMapSummary)}
									chartTitle={chartTitle}
									weekFilter={weekFilter}
									category={category}
									isNetSales={true}
									selectedTab={selectedTab}
								/>
							</Typography>
						</Box>
					</Box>
					<Box className={classes.mapContainer}>
						<Box
							sx={{
								display: 'flex',
								alignContent: 'center',
								justifyContent: 'space-between',
								'@media only screen and (min-width: 780px)': {
									flexDirection: 'row',
								},
								'@media only screen and (max-width: 779px)': {
									flexDirection: 'column-reverse',
								},
							}}>
							<Box className={classes.mapLeftContainer}>
								<ToggleButtonGroup
									className={classes.toggleButtonGroup}
									exclusive
									value={toggleValue}
									onChange={handleToggleBtn}>
									<ToggleButton value="percentage" aria-label="left aligned">
										% Chg
									</ToggleButton>
									<ToggleButton value="dollar" aria-label="centered">
										$ Chg
									</ToggleButton>
								</ToggleButtonGroup>

								<Box
									display={'flex'}
									gap={'0px'}
									minWidth={'468px'}
									justifyContent={'flex-start'}
									marginRight={'10px'}>
									{/* Top 5 States */}
									<Box sx={{ marginRight: '40px', width: '100%' }}>
										<Typography style={styles.topAndBottomTitle}>
											Top 5 states
										</Typography>
										<Box>
											{isPercentageChange
												? netSalesMapSummary.yoyTopStates?.map((e, index) => {
														const yoyValue = handleNullorUndefined(
															e?.properties?.yoyValue
														);
														let value;
														let isPositive;
														if (
															yoyValue === 'NA' ||
															yoyValue === '-' ||
															yoyValue === -999999
														) {
															isPositive = false;
															value = '-';
														} else {
															isPositive = e?.properties?.yoyValue >= 0;
															value = isPositive
																? `${prefix}${e?.properties?.yoyValue.toLocaleString(
																		'en-US'
																  )}${suffix}`
																: `-${prefix}${Math.abs(
																		e?.properties?.yoyValue
																  ).toLocaleString('en-US')}${suffix}`;
														}
														return (
															<Box
																display={'flex'}
																sx={{
																	'@media only screen and (min-width: 780px)': {
																		gap: '16px',
																	},
																	'@media only screen and (max-width: 779px)': {
																		gap: '32px',
																	},
																}}
																justifyContent={'space-between'}
																key={index}>
																{value !== '-' && (
																	<Typography style={styles.topAndBottomText}>
																		{e?.properties?.name}
																	</Typography>
																)}
																<Typography
																	style={styles.topAndBottomText}
																	color={
																		isPositive
																			? SUCCESS_MD
																			: value === '-'
																			? BLACK
																			: ERROR_MD
																	}>
																	{value}
																</Typography>
															</Box>
														);
												  })
												: netSalesMapSummary.absYoyTopStates?.map(
														(e, index) => {
															const absValue = handleNullorUndefined(
																e?.properties?.absYoY
															);
															let value;
															let isPositive;
															if (
																absValue === 'NA' ||
																absValue === '-' ||
																absValue === -999999
															) {
																isPositive = false;
																value = '-';
															} else {
																isPositive = e?.properties?.absYoY >= 0;
																value = isPositive
																	? `${prefix}${e?.properties?.absYoY.toLocaleString(
																			'en-US'
																	  )}${suffix}`
																	: `-${prefix}${Math.abs(
																			e?.properties?.absYoY
																	  ).toLocaleString('en-US')}${suffix}`;
															}

															return (
																<Box
																	display={'flex'}
																	sx={{
																		'@media only screen and (min-width: 780px)':
																			{
																				gap: '16px',
																			},
																		'@media only screen and (max-width: 779px)':
																			{
																				gap: '32px',
																			},
																	}}
																	justifyContent={'space-between'}
																	key={index}>
																	{value !== '-' && (
																		<Typography style={styles.topAndBottomText}>
																			{e?.properties?.name}
																		</Typography>
																	)}

																	<Typography
																		style={styles.topAndBottomText}
																		color={
																			isPositive
																				? SUCCESS_MD
																				: value === '-'
																				? BLACK
																				: ERROR_MD
																		}>
																		{value}
																	</Typography>
																</Box>
															);
														}
												  )}
										</Box>
									</Box>
									{/* Bottom 5 States */}
									<Box sx={{ width: '100%' }}>
										<Typography style={styles.topAndBottomTitle}>
											Bottom 5 states
										</Typography>
										{isPercentageChange
											? netSalesMapSummary.yoyBottomStates?.map((e, index) => {
													const yoyValue = handleNullorUndefined(
														e?.properties?.yoyValue
													);
													let value;
													let isPositive;
													if (
														yoyValue === 'NA' ||
														yoyValue === '-' ||
														yoyValue === -999999
													) {
														isPositive = false;
														value = '-';
													} else {
														isPositive = e?.properties?.yoyValue >= 0;
														value = isPositive
															? `${prefix}${e?.properties?.yoyValue.toLocaleString(
																	'en-US'
															  )}${suffix}`
															: `-${prefix}${Math.abs(
																	e?.properties?.yoyValue
															  ).toLocaleString('en-US')}${suffix}`;
													}
													return (
														<Box
															display={'flex'}
															justifyContent={'space-between'}
															sx={{
																'@media only screen and (min-width: 780px)': {
																	gap: '16px',
																},
																'@media only screen and (max-width: 779px)': {
																	gap: '32px',
																},
															}}
															key={index}>
															{value !== '-' && (
																<Typography style={styles.topAndBottomText}>
																	{e?.properties?.name}
																</Typography>
															)}
															<Typography
																style={styles.topAndBottomText}
																color={
																	isPositive
																		? SUCCESS_MD
																		: value === '-'
																		? BLACK
																		: ERROR_MD
																}>
																{value}
															</Typography>
														</Box>
													);
											  })
											: netSalesMapSummary.absYoyBottomStates?.map(
													(e, index) => {
														const absValue = handleNullorUndefined(
															e?.properties?.absYoY
														);
														let value;
														let isPositive;
														if (
															absValue === 'NA' ||
															absValue === '-' ||
															absValue === -999999
														) {
															isPositive = false;
															value = '-';
														} else {
															isPositive = e?.properties?.absYoY >= 0;
															value = isPositive
																? `${prefix}${e?.properties?.absYoY.toLocaleString(
																		'en-US'
																  )}${suffix}`
																: `-${prefix}${Math.abs(
																		e?.properties?.absYoY
																  ).toLocaleString('en-US')}${suffix}`;
														}

														return (
															<Box
																display={'flex'}
																sx={{
																	'@media only screen and (min-width: 780px)': {
																		gap: '16px',
																	},
																	'@media only screen and (max-width: 779px)': {
																		gap: '32px',
																	},
																}}
																justifyContent={'space-between'}
																key={index}>
																{value !== '-' && (
																	<Typography style={styles.topAndBottomText}>
																		{e?.properties?.name}
																	</Typography>
																)}
																<Typography
																	style={styles.topAndBottomText}
																	color={
																		isPositive
																			? SUCCESS_MD
																			: value === '-'
																			? BLACK
																			: ERROR_MD
																	}>
																	{value}
																</Typography>
															</Box>
														);
													}
											  )}
									</Box>
								</Box>
							</Box>

							{/* Map */}
							<Box sx={{ width: '100%', height: '350px' }}>{map}</Box>
						</Box>
						{/* Map legend */}
						<MapLegend
							slots={{
								NEGATIVE_SLOTS,
								POSITIVE_SLOTS,
							}}
							prefix={prefix}
							suffix={suffix}
							legendMin={min}
							legendMax={max}
						/>
					</Box>
				</Fragment>
			)}
		</Fragment>
	);
}

const MapLegend = ({
	slots: { NEGATIVE_SLOTS, POSITIVE_SLOTS },
	prefix,
	suffix,
	legendMin,
	legendMax,
}) => {
	const legendHasTwoParts = NEGATIVE_SLOTS.length + POSITIVE_SLOTS.length === 6;
	const legendText = `Net sales year over year ${prefix}${suffix} change`;
	return (
		<Box sx={styles.legendContainer}>
			<Typography style={styles.legendText}>{legendText}</Typography>
			<Box display={'flex'}>
				{NEGATIVE_SLOTS?.map((range, index) => {
					const title = `${formatNumberWithUnits(
						range[0],
						prefix,
						suffix
					)} - ${formatNumberWithUnits(range[1], prefix, suffix)}`;
					return (
						<Tooltip key={index} title={title} placement="top" arrow>
							<Box
								key={index}
								sx={{
									...styles.legendBlock,
									background: NEGATIVE_COLORS[index],
								}}
							/>
						</Tooltip>
					);
				})}
				{POSITIVE_SLOTS?.map((range, index) => {
					const title = `${formatNumberWithUnits(
						range[0],
						prefix,
						suffix
					)} - ${formatNumberWithUnits(range[1], prefix, suffix)}`;
					return (
						<Tooltip key={index} title={title} placement="top" arrow>
							<Box
								key={index}
								sx={{
									...styles.legendBlock,
									background: POSITIVE_COLORS[index],
								}}
							/>
						</Tooltip>
					);
				})}
			</Box>
			<Box display={'flex'} gap={'68px'}>
				<Typography style={styles.legendLabel}>
					{formatNumberWithUnits(legendMin, prefix, suffix, true)}
				</Typography>
				{legendHasTwoParts ? (
					<Typography style={styles.legendLabel}>0</Typography>
				) : null}
				<Typography style={styles.legendLabel}>
					{formatNumberWithUnits(legendMax, prefix, suffix, true)}
				</Typography>
			</Box>
		</Box>
	);
};

MapLegend.propTypes = {
	slots: PropTypes.object,
	prefix: PropTypes.string,
	suffix: PropTypes.string,
	legendMin: PropTypes.number,
	legendMax: PropTypes.number,
};

const NoDataComponent = ({ apiKey }) => {
	const classes = useMapSummaryStyles();
	return (
		<Box>
			<Box className={classes.header}>
				<Box>
					<Typography style={styles.title}>{chartTitle}</Typography>
					<Typography style={styles.value}>$ --</Typography>
				</Box>
			</Box>
			<Box className={classes.mapContainer}>
				<Box
					display={'flex'}
					alignItems={'center'}
					justifyContent={'space-around'}>
					<Box>
						<ToggleButtonGroup
							className={classes.toggleButtonGroup}
							exclusive
							value={'percentage'}>
							<ToggleButton value="percentage" aria-label="left aligned">
								% Chg
							</ToggleButton>
							<ToggleButton value="dollar" aria-label="centered">
								$ Chg
							</ToggleButton>
						</ToggleButtonGroup>

						<Typography
							paragraph
							sx={{ fontSize: '12px', fontFamily: BOGLE_REGULAR }}>
							No data available
						</Typography>
					</Box>
					<Box>
						<MapState
							apiKey={apiKey}
							width={'100%'}
							height={'100%'}
							tooltip={() => {}}
							onHover={() => {}}
							fillColor={() => [144, 145, 150]}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

NoDataComponent.propTypes = {
	apiKey: PropTypes.string,
};

ItemHeatMap.propTypes = {
	itemNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	category: PropTypes.string,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	selectedTab: PropTypes.string,
};
export default ItemHeatMap;
